/**
 * AIConfidenceBadge - Sprint 4, F4.6
 * ===================================
 *
 * Single canonical visual badge for any AI suggestion / classification.
 * Replaces ad-hoc "92% confidence" text strings scattered across the app.
 *
 * Usage:
 *   <AIConfidenceBadge score={0.92} />
 *   <AIConfidenceBadge score={0.62} reason="Matched 'KEPCO' → Utilities · Electricity" />
 *   <AIConfidenceBadge score={0.45} compact />
 *
 * Confidence buckets:
 *   ≥ 0.80   High      green
 *   0.65–0.79 Medium   amber
 *   < 0.65   Low      slate
 */
import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { Sparkles, HelpCircle } from 'lucide-react';

function _bucket(n) {
  if (n >= 0.80) return { label: 'High', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (n >= 0.65) return { label: 'Medium', tone: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { label: 'Low', tone: 'bg-slate-50 text-slate-600 border-slate-200' };
}

const AIConfidenceBadge = ({
  score,
  reason,
  compact = false,
  showIcon = true,
  className = '',
  testId,
}) => {
  if (score === null || score === undefined || Number.isNaN(score)) return null;
  const pct = Math.round(score * 100);
  const b = _bucket(score);
  const testIdResolved = testId || `ai-confidence-${b.label.toLowerCase()}`;

  const pill = (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${b.tone} ${className}`}
      data-testid={testIdResolved}
    >
      {showIcon && <Sparkles className="w-3 h-3" aria-hidden="true" />}
      {compact ? <span>{pct}%</span> : <span>{b.label} · {pct}%</span>}
      {reason && <HelpCircle className="w-3 h-3 ml-0.5 opacity-60" aria-hidden="true" />}
    </span>
  );

  if (!reason) return pill;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {pill}
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs leading-snug">
            <span className="font-semibold">Why AkountFly suggested this:</span>{' '}
            {reason}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default AIConfidenceBadge;
export { AIConfidenceBadge };
